import React, { useState, useEffect, useRef, useMemo } from 'react';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';

/**
 * Tab-Delimited Editor Component
 * Provides grid view editing for TSV files
 * Maps edits back to TSV text format
 * Supports large files with virtualized rendering
 */

const TSVEditor = ({ file, onSave, onClose }) => {
  const [data, setData] = useState([]);
  const [headers, setHeaders] = useState([]);
  const [originalData, setOriginalData] = useState([]);
  const [isSaving, setIsSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [selectedCell, setSelectedCell] = useState(null);
  const [editingCell, setEditingCell] = useState(null);
  const [editValue, setEditValue] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [isMaximized, setIsMaximized] = useState(false);
  const tableRef = useRef(null);
  const editInputRef = useRef(null);

  useEffect(() => {
    if (file && file.content) {
      let fileData = [];
      
      // Handle pre-parsed array data
      if (Array.isArray(file.content)) {
        fileData = file.content;
      }
      // Handle raw string content (fallback)
      else if (typeof file.content === 'string') {
        // Try to parse as TSV/CSV
        try {
          const lines = file.content.trim().split('\n');
          if (lines.length > 1) {
            const delimiter = lines[0].includes('\t') ? '\t' : ',';
            const headers = lines[0].split(delimiter).map(h => h.trim().replace(/^"|"$/g, ''));
            
            fileData = [];
            for (let i = 1; i < lines.length; i++) {
              const values = lines[i].split(delimiter);
              const row = {};
              
              headers.forEach((header, index) => {
                let value = values[index] || '';
                value = value.replace(/^"|"$/g, '');
                const numValue = parseFloat(value);
                row[header] = isNaN(numValue) ? value : numValue;
              });
              
              fileData.push(row);
            }
          }
        } catch (error) {
          console.error('Failed to parse TSV/CSV content:', error);
        }
      }
      
      if (fileData.length > 0) {
        const fileHeaders = Object.keys(fileData[0]);
        setHeaders(fileHeaders);
        setData(fileData);
        setOriginalData(JSON.parse(JSON.stringify(fileData))); // Deep clone
        setHasChanges(false);
      }
    }
  }, [file]);

  // Viewport management is no longer needed with standard table rendering

  useEffect(() => {
    // Check for changes
    setHasChanges(JSON.stringify(data) !== JSON.stringify(originalData));
  }, [data, originalData]);

  useEffect(() => {
    if (editingCell && editInputRef.current) {
      editInputRef.current.focus();
      editInputRef.current.select();
    }
  }, [editingCell]);

  // Filter and sort data
  const processedData = useMemo(() => {
    let filtered = data.map((row, index) => ({ ...row, _originalIndex: index }));

    // Apply search filter
    if (searchTerm) {
      filtered = filtered.filter(row =>
        Object.values(row).some(value =>
          String(value).toLowerCase().includes(searchTerm.toLowerCase())
        )
      );
    }

    // Apply sorting
    if (sortConfig.key) {
      filtered = [...filtered].sort((a, b) => {
        const aValue = a[sortConfig.key];
        const bValue = b[sortConfig.key];
        
        if (aValue === null || aValue === undefined) return 1;
        if (bValue === null || bValue === undefined) return -1;
        
        let comparison = 0;
        if (typeof aValue === 'number' && typeof bValue === 'number') {
          comparison = aValue - bValue;
        } else {
          comparison = String(aValue).localeCompare(String(bValue));
        }
        
        return sortConfig.direction === 'desc' ? -comparison : comparison;
      });
    }

    return filtered;
  }, [data, searchTerm, sortConfig]);

  // Virtualized rendering is now disabled - using processedData directly

  const handleSave = async () => {
    if (!hasChanges || isSaving) return;

    setIsSaving(true);
    
    try {
      // Convert data array to TSV format before saving
      const tsvContent = [
        headers.join('\t'),
        ...data.map(row => headers.map(header => {
          const value = row[header];
          return typeof value === 'string' ? value : String(value);
        }).join('\t'))
      ].join('\n');
      
      await onSave(file, tsvContent);
      setOriginalData(JSON.parse(JSON.stringify(data)));
      setHasChanges(false);
      toast.success(`Saved ${file.name}`);
    } catch (error) {
      toast.error(`Failed to save ${file.name}: ${error.message}`);
    } finally {
      setIsSaving(false);
    }
  };

  const handleCellClick = (originalIndex, colIndex) => {
    setSelectedCell({ row: originalIndex, col: colIndex });
  };

  const handleCellDoubleClick = (rowIndex, colIndex) => {
    const value = processedData[rowIndex]?.[headers[colIndex]];
    const originalIndex = processedData[rowIndex]?._originalIndex;
    setEditingCell({ row: originalIndex, col: colIndex, displayRow: rowIndex });
    setEditValue(value !== null && value !== undefined ? String(value) : '');
  };

  const handleCellEdit = (newValue) => {
    if (!editingCell) return;

    const newData = [...data];
    const header = headers[editingCell.col];
    
    // Try to convert to number if possible
    const numValue = parseFloat(newValue);
    newData[editingCell.row][header] = isNaN(numValue) ? newValue : numValue;
    
    setData(newData);
  };

  const handleEditComplete = () => {
    handleCellEdit(editValue);
    setEditingCell(null);
    setEditValue('');
  };

  const handleEditCancel = () => {
    setEditingCell(null);
    setEditValue('');
  };

  const handleKeyDown = (e) => {
    if (editingCell) {
      if (e.key === 'Enter') {
        e.preventDefault();
        handleEditComplete();
      } else if (e.key === 'Escape') {
        e.preventDefault();
        handleEditCancel();
      } else if (e.key === 'Tab') {
        e.preventDefault();
        handleEditComplete();
        // Move to next cell
        const nextCol = editingCell.col + 1;
        if (nextCol < headers.length) {
          // Find the original index for the next cell
          const displayRow = editingCell.displayRow !== undefined ? editingCell.displayRow : editingCell.row;
          const originalIndex = processedData[displayRow]?._originalIndex;
          if (originalIndex !== undefined) {
            setEditingCell({ row: originalIndex, col: nextCol, displayRow: displayRow });
            const nextValue = processedData[displayRow]?.[headers[nextCol]];
            setEditValue(nextValue !== null && nextValue !== undefined ? String(nextValue) : '');
          }
        }
      }
    } else {
      // Navigation shortcuts
      if (e.key === 'Escape') {
        if (hasChanges) {
          if (window.confirm('You have unsaved changes. Are you sure you want to close?')) {
            onClose();
          }
        } else {
          onClose();
        }
      } else if ((e.ctrlKey || e.metaKey) && e.key === 's') {
        e.preventDefault();
        handleSave();
      }
    }
  };

  const handleSort = (header) => {
    setSortConfig(prev => ({
      key: header,
      direction: prev.key === header && prev.direction === 'asc' ? 'desc' : 'asc'
    }));
  };

  const handleAddRow = () => {
    const newRow = {};
    headers.forEach(header => {
      newRow[header] = '';
    });
    const newData = [...data, newRow];
    setData(newData);
    // Clear any filters and sort to show the new row
    setSearchTerm('');
    setSortConfig({ key: null, direction: 'asc' });
  };

  const handleDeleteRow = (displayIndex) => {
    const originalIndex = processedData[displayIndex]?._originalIndex;
    if (originalIndex !== undefined) {
      const newData = data.filter((_, i) => i !== originalIndex);
      setData(newData);
    }
  };

  const handleAddColumn = () => {
    const newColumnName = window.prompt('Enter column name:');
    if (!newColumnName) return;

    const newHeaders = [...headers, newColumnName];
    setHeaders(newHeaders);
    
    const newData = data.map(row => ({
      ...row,
      [newColumnName]: ''
    }));
    setData(newData);
  };

  const handleDeleteColumn = (header) => {
    if (!window.confirm(`Delete column "${header}"?`)) return;

    const newHeaders = headers.filter(h => h !== header);
    setHeaders(newHeaders);
    
    const newData = data.map(row => {
      const newRow = { ...row };
      delete newRow[header];
      return newRow;
    });
    setData(newData);
  };

  // Virtualized rendering is now disabled - using standard table rendering
  // const handleScroll = (e) => {
  //   const scrollTop = e.target.scrollTop;
  //   const start = Math.floor(scrollTop / ROW_HEIGHT);
  //   const end = Math.min(start + VIEWPORT_SIZE + 5, data.length); // Buffer for smooth scrolling
  //   setViewportStart(start);
  //   setViewportEnd(end);
  // };

  const exportToTSV = () => {
    const tsvContent = [
      headers.join('\t'),
      ...data.map(row => headers.map(header => row[header] || '').join('\t'))
    ].join('\n');

    const blob = new Blob([tsvContent], { type: 'text/tab-separated-values' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = file.name.replace(/\.[^/.]+$/, '') + '_exported.tsv';
    a.click();
    URL.revokeObjectURL(url);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.95 }}
      animate={{ opacity: 1, scale: 1 }}
      exit={{ opacity: 0, scale: 0.95 }}
      className={`theme-modal-backdrop fixed inset-0 flex items-center justify-center z-50 p-4 ${
        isMaximized ? 'p-0' : ''
      }`}
      onClick={onClose}
      onKeyDown={handleKeyDown}
      tabIndex={0}
    >
      <motion.div
        initial={{ y: 20 }}
        animate={{ y: 0 }}
        className={`theme-modal rounded-lg shadow-2xl flex flex-col ${
          isMaximized
            ? 'w-full h-full rounded-none'
            : 'w-full max-w-8xl max-h-[95vh]'
        }`}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-gray-200">
          <div className="flex items-center space-x-3">
            <div className="w-8 h-8 bg-green-100 rounded-lg flex items-center justify-center">
              <svg className="w-5 h-5 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 17V7m0 10a2 2 0 01-2 2H5a2 2 0 01-2-2V7a2 2 0 012-2h2a2 2 0 012 2m0 10a2 2 0 002 2h2a2 2 0 002-2M9 7a2 2 0 012-2h2a2 2 0 012 2m0 10V7m0 10a2 2 0 002 2h2a2 2 0 002-2V7a2 2 0 00-2-2h-2a2 2 0 00-2 2" />
              </svg>
            </div>
            <div>
              <h2 className="text-lg font-semibold text-gray-900">{file.name}</h2>
              <p className="text-sm text-gray-500">
                TSV Editor - {data.length} rows, {headers.length} columns
              </p>
            </div>
          </div>
          
          <div className="flex items-center space-x-2">
            {hasChanges && (
              <span className="text-xs bg-yellow-100 text-yellow-800 px-2 py-1 rounded">
                Unsaved changes
              </span>
            )}
            <button
              onClick={() => setIsMaximized(!isMaximized)}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
              title={isMaximized ? "Restore" : "Maximize"}
            >
              {isMaximized ? (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 9V4.5M9 9H4.5M9 9L3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5l5.25 5.25" />
                </svg>
              ) : (
                <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 8V4m0 0h4M4 4l5 5m11-1V4m0 0h-4m4 0l-5 5M4 16v4m0 0h4m-4 0l5-5m11 5l-5-5m5 5v-4m0 4h-4" />
                </svg>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <svg className="w-5 h-5 text-gray-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Toolbar */}
        <div className="flex items-center justify-between p-3 border-b border-gray-200 bg-gray-50">
          <div className="flex items-center space-x-2">
            <input
              type="text"
              placeholder="Search..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="px-3 py-1.5 text-sm border border-gray-300 rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            
            <button
              onClick={handleAddRow}
              className="px-3 py-1.5 text-sm bg-green-600 hover:bg-green-700 text-white rounded transition-colors"
            >
              Add Row
            </button>
            
            <button
              onClick={handleAddColumn}
              className="px-3 py-1.5 text-sm bg-blue-600 hover:bg-blue-700 text-white rounded transition-colors"
            >
              Add Column
            </button>
            
            <button
              onClick={exportToTSV}
              className="px-3 py-1.5 text-sm bg-gray-600 hover:bg-gray-700 text-white rounded transition-colors"
            >
              Export
            </button>
          </div>

          <div className="flex items-center space-x-2">
            <button
              onClick={handleSave}
              disabled={!hasChanges || isSaving}
              className={`px-4 py-1.5 text-sm rounded transition-colors ${
                hasChanges && !isSaving
                  ? 'bg-blue-600 hover:bg-blue-700 text-white'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {isSaving ? 'Saving...' : 'Save'}
            </button>
          </div>
        </div>

        {/* Table */}
        <div
          ref={tableRef}
          className="flex-1 overflow-auto border border-gray-200"
          style={{ height: isMaximized ? 'calc(100vh - 200px)' : '500px' }}
        >
          <table className="w-full text-sm">
            <thead className="bg-gray-50 sticky top-0 z-10">
              <tr>
                <th className="px-4 py-2 text-left font-medium text-gray-900 border-b border-gray-200 w-12">
                  #
                </th>
                {headers.map((header, index) => (
                  <th
                    key={header}
                    className="px-4 py-2 text-left font-medium text-gray-900 border-b border-gray-200 cursor-pointer hover:bg-gray-100 group relative"
                    onClick={() => handleSort(header)}
                  >
                    <div className="flex items-center justify-between">
                      <span>{header}</span>
                      <div className="flex flex-col">
                        <svg
                          className={`w-3 h-3 text-gray-400 ${sortConfig.key === header && sortConfig.direction === 'asc' ? 'text-blue-600' : ''}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M14.707 12.707a1 1 0 01-1.414 0L10 9.414l-3.293 3.293a1 1 0 01-1.414-1.414l4-4a1 1 0 011.414 0l4 4a1 1 0 010 1.414z" clipRule="evenodd" />
                        </svg>
                        <svg
                          className={`w-3 h-3 text-gray-400 -mt-1 ${sortConfig.key === header && sortConfig.direction === 'desc' ? 'text-blue-600' : ''}`}
                          fill="currentColor"
                          viewBox="0 0 20 20"
                        >
                          <path fillRule="evenodd" d="M5.293 7.293a1 1 0 011.414 0L10 10.586l3.293-3.293a1 1 0 111.414 1.414l-4 4a1 1 0 01-1.414 0l-4-4a1 1 0 010-1.414z" clipRule="evenodd" />
                        </svg>
                      </div>
                    </div>
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        handleDeleteColumn(header);
                      }}
                      className="absolute right-1 top-1/2 transform -translate-y-1/2 opacity-0 group-hover:opacity-100 p-1 hover:bg-red-100 rounded"
                      title="Delete column"
                    >
                      <svg className="w-3 h-3 text-red-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                      </svg>
                    </button>
                  </th>
                ))}
                <th className="px-4 py-2 text-left font-medium text-gray-900 border-b border-gray-200 w-12">
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {processedData.map((row, displayIndex) => {
                const originalIndex = row._originalIndex;
                return (
                  <tr
                    key={originalIndex}
                    className={`border-b border-gray-100 hover:bg-gray-50 ${
                      selectedCell?.row === originalIndex ? 'bg-blue-50' : ''
                    }`}
                  >
                    <td className="px-4 py-2 text-gray-500 border-r border-gray-200">
                      {displayIndex + 1}
                    </td>
                    {headers.map((header, colIndex) => (
                      <td
                        key={`${originalIndex}-${colIndex}`}
                        className={`px-4 py-2 border-r border-gray-200 cursor-pointer ${
                          selectedCell?.row === originalIndex && selectedCell?.col === colIndex
                            ? 'bg-blue-100'
                            : ''
                        } ${
                          editingCell?.row === originalIndex && editingCell?.col === colIndex
                            ? 'p-0'
                            : ''
                        }`}
                        onClick={() => handleCellClick(originalIndex, colIndex)}
                        onDoubleClick={() => handleCellDoubleClick(displayIndex, colIndex)}
                      >
                        {editingCell?.row === originalIndex && editingCell?.col === colIndex ? (
                          <input
                            ref={editInputRef}
                            type="text"
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            onBlur={handleEditComplete}
                            onKeyDown={(e) => {
                              if (e.key === 'Enter') {
                                e.preventDefault();
                                handleEditComplete();
                              } else if (e.key === 'Escape') {
                                e.preventDefault();
                                handleEditCancel();
                              }
                            }}
                            className="w-full h-full px-2 py-1 border-2 border-blue-500 rounded focus:outline-none"
                            style={{ fontSize: '14px' }}
                          />
                        ) : (
                          <span className={typeof row[header] === 'number' ? 'text-blue-600 font-mono' : ''}>
                            {row[header] !== null && row[header] !== undefined ? String(row[header]) : ''}
                          </span>
                        )}
                      </td>
                    ))}
                    <td className="px-4 py-2">
                      <button
                        onClick={() => handleDeleteRow(displayIndex)}
                        className="p-1 hover:bg-red-100 rounded text-red-600"
                        title="Delete row"
                      >
                        <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                        </svg>
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        {/* Status Bar */}
        <div className="flex items-center justify-between p-2 border-t border-gray-200 text-xs text-gray-500 bg-gray-50">
          <div>
            {processedData.length} of {data.length} rows {searchTerm && '(filtered)'}
          </div>
          <div>
            {hasChanges ? 'Unsaved' : 'Saved'} | Double-click to edit | Ctrl+S to save | Esc to close
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
};

export default TSVEditor;
